// ─── Sélecteur multi-comptes (tap sur l'avatar du Profil) ─────────────────────
// Liste les comptes connus sur cet appareil et bascule sans ressaisie
// (cf. auth/comptes.js). Ajout d'un compte (une saisie e-mail/mdp) ou retrait.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, View, Text, TouchableOpacity, TextInput, ActivityIndicator, ScrollView,
  StyleSheet, Platform, KeyboardAvoidingView,
} from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import {
  listeComptes, memoriserCompteCourant, basculerVers, ajouterCompteEmail,
  retirerCompte, compteActifUid,
} from './comptes';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const couleurDe = (s) => {
  const p = ['#6C4FE6', '#E6774F', '#4FA9E6', '#3FB27F', '#E6B23F', '#C74FE6'];
  let h = 0; for (const c of (s || '?')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return p[h % p.length];
};

function Avatar({ prenom, email, couleur, actif }) {
  const lettre = (prenom || email || '?')[0].toUpperCase();
  const bg = couleur || couleurDe(email || prenom);
  return (
    <View style={[st.avatar, { backgroundColor: bg }, actif && st.avatarActif]}>
      <Text style={st.avatarLettre}>{lettre}</Text>
    </View>
  );
}

export default function BasculeComptes({ visible, onClose, user, onDeconnexion }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [busyUid, setBusyUid] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ajout, setAjout] = useState(false);
  const [email, setEmail] = useState('');
  const [mdp, setMdp] = useState('');
  const [voir, setVoir] = useState(false);
  const [ajoutBusy, setAjoutBusy] = useState(false);

  const actifUid = compteActifUid();

  const recharger = useCallback(async () => {
    setChargement(true);
    try { await memoriserCompteCourant({ email: user?.email, prenom: user?.prenom }); } catch {}
    try { setComptes(await listeComptes()); } catch { setComptes([]); }
    setChargement(false);
  }, [user?.email, user?.prenom]);

  useEffect(() => {
    if (visible) { setErreur(''); setAjout(false); setEmail(''); setMdp(''); setVoir(false); setBusyUid(null); recharger(); }
  }, [visible, recharger]);

  async function basculer(uid) {
    if (uid === actifUid) { onClose?.(); return; }
    setErreur(''); setBusyUid(uid);
    try { await basculerVers(uid); setBusyUid(null); onClose?.(); }
    catch (e) {
      setBusyUid(null);
      setErreur(e?.message === 'SESSION_EXPIREE' ? t('comptes.sessionExpiree') : (e?.message || t('comptes.basculeImpossible')));
    }
  }
  async function retirer(uid) {
    setErreur('');
    try {
      const r = await retirerCompte(uid);
      if (r === 'DECONNEXION') { onClose?.(); onDeconnexion?.(); return; }
      if (r === 'BASCULE') { onClose?.(); return; }
      setComptes(await listeComptes());
    } catch (e) { setErreur(e?.message || t('comptes.retraitImpossible')); }
  }
  async function validerAjout() {
    setErreur('');
    if (!email.trim() || !mdp) { setErreur(t('comptes.identifiantsRequis')); return; }
    setAjoutBusy(true);
    try { await ajouterCompteEmail(email, mdp); setAjoutBusy(false); onClose?.(); }
    catch (e) { setAjoutBusy(false); setErreur(e?.message || t('comptes.connexionImpossible')); }
  }

  const Enveloppe = Platform.OS === 'web' ? View : KeyboardAvoidingView;
  const envProps = Platform.OS === 'web' ? {} : { behavior: Platform.OS === 'ios' ? 'padding' : undefined };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Enveloppe style={{ flex: 1 }} {...envProps}>
        <TouchableOpacity style={s.overlay} activeOpacity={1} onPress={onClose}>
          <TouchableOpacity style={s.carte} activeOpacity={1} onPress={() => {}}>
            <View style={s.entete}>
              <Text style={s.titre}>{t('comptes.titre')}</Text>
              <TouchableOpacity onPress={onClose} hitSlop={HIT}><Text style={s.fermer}>✕</Text></TouchableOpacity>
            </View>

            {chargement ? (
              <ActivityIndicator color={UI.accent} style={{ marginVertical: 24 }} />
            ) : (
              <ScrollView style={{ maxHeight: 360 }} keyboardShouldPersistTaps="handled">
                {/* Ordre alphabetique : la liste des comptes ne doit pas
                    changer d'ordre entre deux ouvertures. */}
                {[...comptes].sort((a, b) =>
                  (a.prenom || a.email || '').localeCompare(b.prenom || b.email || '', 'fr', { sensitivity: 'base' })
                  || (a.email || '').localeCompare(b.email || '', 'fr', { sensitivity: 'base' })
                ).map(c => {
                  const actif = c.uid === actifUid;
                  return (
                    <View key={c.uid} style={[s.ligne, actif && s.ligneActive]}>
                      <TouchableOpacity style={s.ligneGauche} activeOpacity={0.7} disabled={!!busyUid} onPress={() => basculer(c.uid)}>
                        <Avatar prenom={c.prenom} email={c.email} couleur={c.couleur} actif={actif} />
                        <View style={{ flex: 1, marginLeft: 12 }}>
                          <Text style={s.lignePrenom} numberOfLines={1}>{c.prenom || c.email}</Text>
                          <Text style={s.ligneEmail} numberOfLines={1}>{c.email}</Text>
                        </View>
                        {busyUid === c.uid ? <ActivityIndicator color={UI.accent} />
                          : <Text style={[s.marque, actif && { color: UI.accent }]}>{actif ? '✓' : '⇄'}</Text>}
                      </TouchableOpacity>
                      <TouchableOpacity style={s.retirer} hitSlop={HIT} disabled={!!busyUid} onPress={() => retirer(c.uid)} accessibilityLabel={t('comptes.retirerCompte')}>
                        <Text style={s.retirerTxt}>⏻</Text>
                      </TouchableOpacity>
                    </View>
                  );
                })}

                {ajout ? (
                  <View style={s.ajoutZone}>
                    <TextInput style={s.input} placeholder={t('comptes.email')} placeholderTextColor={UI.faint}
                      autoCapitalize="none" keyboardType="email-address" value={email} onChangeText={setEmail} editable={!ajoutBusy} />
                    <View style={s.champMdp}>
                      <TextInput style={s.inputMdp} placeholder={t('comptes.motDePasse')} placeholderTextColor={UI.faint}
                        secureTextEntry={!voir} autoCapitalize="none" autoCorrect={false} value={mdp} onChangeText={setMdp} editable={!ajoutBusy} />
                      <TouchableOpacity style={s.oeil} onPress={() => setVoir(v => !v)} hitSlop={HIT}
                        accessibilityLabel={voir ? t('comptes.masquerMdp') : t('comptes.afficherMdp')}>
                        <Text style={s.oeilTxt}>{voir ? '🙈' : '👁️'}</Text>
                      </TouchableOpacity>
                    </View>
                    <View style={s.ajoutBtns}>
                      <TouchableOpacity style={s.annuler} onPress={() => { setAjout(false); setErreur(''); }} disabled={ajoutBusy}>
                        <Text style={s.annulerTxt}>{t('commun.annuler')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={s.valider} onPress={validerAjout} disabled={ajoutBusy}>
                        {ajoutBusy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={s.validerTxt}>{t('login.seConnecter')}</Text>}
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <TouchableOpacity style={s.ajouter} onPress={() => { setAjout(true); setErreur(''); }} disabled={!!busyUid}>
                    <Text style={s.ajouterIc}>＋</Text>
                    <Text style={s.ajouterTxt}>{t('comptes.ajouterCompte')}</Text>
                  </TouchableOpacity>
                )}

                {!!erreur && <Text style={s.erreur}>{erreur}</Text>}
                <Text style={s.note}>{t('comptes.note')}</Text>
              </ScrollView>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Enveloppe>
    </Modal>
  );
}

const st = StyleSheet.create({
  avatar:       { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  avatarActif:  { borderWidth: 2, borderColor: '#fff' },
  avatarLettre: { color: '#fff', fontSize: 17, fontWeight: '800' },
});
const makeStyles = (UI) => StyleSheet.create({
  overlay:     { flex: 1, backgroundColor: UI.overlay, justifyContent: 'center', alignItems: 'center', padding: 20 },
  carte:       { width: '100%', maxWidth: 420, backgroundColor: UI.card, borderRadius: 18, padding: 18 },
  entete:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  titre:       { fontSize: 17, fontWeight: '800', color: UI.ink },
  fermer:      { fontSize: 18, color: UI.muted },
  ligne:       { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 8, borderRadius: 12 },
  ligneActive: { backgroundColor: UI.accentTint },
  ligneGauche: { flex: 1, flexDirection: 'row', alignItems: 'center' },
  lignePrenom: { fontSize: 14, fontWeight: '700', color: UI.ink },
  ligneEmail:  { fontSize: 12, color: UI.faint },
  marque:      { fontSize: 17, color: UI.faint, fontWeight: '800', width: 24, textAlign: 'center' },
  retirer:     { paddingHorizontal: 6, paddingVertical: 6, marginLeft: 4 },
  retirerTxt:  { fontSize: 16, color: UI.danger },
  ajouter:     { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 8, marginTop: 4 },
  ajouterIc:   { fontSize: 18, color: UI.accent, fontWeight: '800' },
  ajouterTxt:  { fontSize: 14, fontWeight: '700', color: UI.accent, marginLeft: 8 },
  ajoutZone:   { marginTop: 6, padding: 4 },
  input:       { borderWidth: 1, borderColor: UI.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, marginBottom: 8, color: UI.ink, backgroundColor: UI.champ },
  champMdp:    { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: UI.border, borderRadius: 10, backgroundColor: UI.champ, marginBottom: 8 },
  inputMdp:    { flex: 1, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: UI.ink },
  oeil:        { paddingHorizontal: 12, paddingVertical: 8 },
  oeilTxt:     { fontSize: 16 },
  ajoutBtns:   { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 2 },
  annuler:     { paddingVertical: 10, paddingHorizontal: 14 },
  annulerTxt:  { color: UI.muted, fontWeight: '700' },
  valider:     { backgroundColor: UI.accent, borderRadius: 10, paddingVertical: 10, paddingHorizontal: 18, minWidth: 110, alignItems: 'center' },
  validerTxt:  { color: '#fff', fontWeight: '800' },
  erreur:      { color: UI.danger, fontSize: 14, marginTop: 8, textAlign: 'center' },
  note:        { color: UI.faint, fontSize: 12, marginTop: 12, textAlign: 'center', lineHeight: 16 },
});
